/**
 * BrideShareView: the ONE public, unauthenticated route in the app. No
 * login, view-only, "pretty presentation mode" — per the locked access
 * model: "Can be shown during meetings or sent by link. No editing in
 * Version 1."
 *
 * Renders its own simplified canvas rather than reusing PlannerCanvas
 * directly with editing disabled — this page should never accidentally
 * inherit an edit affordance from a shared component as the editor
 * evolves. A simple, deliberately separate read-only renderer is safer
 * here than "the same canvas, but with props to turn editing off."
 */

import { useParams } from 'react-router-dom';
import { useSharedLayout } from '../api/shareLinks';
import { useRoomDefinitions } from '../api/roomDefinitions';
import { useObjectCatalog } from '../api/inventoryLimits';
import { ReadOnlyLayoutView } from '../components/export/ReadOnlyLayoutView';

const ROOM_LABELS: Record<string, string> = {
  candlelight: 'Candlelight',
  conservatory: 'Conservatory',
  tree_deck: 'Tree Deck',
};

export function BrideShareView() {
  const { token } = useParams<{ token: string }>();
  const { data: shared, isLoading, error } = useSharedLayout(token);
  const { data: rooms } = useRoomDefinitions();
  const { data: catalog } = useObjectCatalog();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-evergreen-700">
        <p className="font-body text-ivory-200">{'Loading your layout\u2026'}</p>
      </div>
    );
  }

  if (error || !shared) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-evergreen-700 px-6 text-center">
        <p className="font-display text-xl text-ivory-50">We couldn&rsquo;t find that layout</p>
        <p className="font-body text-sm text-ivory-200">
          This link may have expired or been replaced with a new one. Please check with your
          venue coordinator.
        </p>
      </div>
    );
  }

  const room = rooms?.find((r) => r.room === shared.room);

  const totalSeats = shared.objects.reduce((sum, o) => sum + (o.seats ?? 0), 0);
  const tableTypes = ['round_table', 'rect_table_6', 'rect_table_8', 'head_table', 'sweetheart_table'];
  const totalTables = shared.objects.filter((o) => tableTypes.includes(o.type)).length;
  const danceFloorEntry = shared.objects.find(
    (o) => o.type === 'dance_floor_15' || o.type === 'dance_floor_18'
  );

  return (
    <div className="min-h-screen bg-evergreen-700">
      <div className="px-6 py-8 text-center">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
          The Venue at Lilly Lou&rsquo;s
        </p>
        <h1 className="mt-2 font-display text-3xl text-ivory-50">{shared.coupleNames}</h1>
        <p className="mt-1 font-body text-sm text-ivory-200">
          {ROOM_LABELS[shared.room] ?? shared.room} {'\u00b7'} {shared.guestCount} guests
        </p>
      </div>

      <div className="mx-auto max-w-4xl rounded-t-xl bg-ivory-50 px-4 py-6 shadow-elevated sm:px-8">
        {room && catalog ? (
          <div className="overflow-x-auto">
            <ReadOnlyLayoutView room={room} objects={shared.objects} catalog={catalog} />
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center">
            <p className="font-body text-sm text-ink-400">{'Loading your layout\u2026'}</p>
          </div>
        )}

        <div className="mt-6 grid grid-cols-3 gap-4 rounded-lg bg-ivory-100 p-4 text-center">
          <div>
            <p className="font-display text-xl text-evergreen-600">{shared.guestCount}</p>
            <p className="font-body text-xs text-ink-400">Guests</p>
          </div>
          <div>
            <p className="font-display text-xl text-evergreen-600">{totalTables}</p>
            <p className="font-body text-xs text-ink-400">Tables</p>
          </div>
          <div>
            <p className="font-display text-xl text-evergreen-600">{totalSeats}</p>
            <p className="font-body text-xs text-ink-400">Seats</p>
          </div>
        </div>

        {danceFloorEntry && (
          <p className="mt-3 text-center font-body text-xs text-ink-400">
            {danceFloorEntry.type === 'dance_floor_18' ? "18' \u00d7 18'" : "15' \u00d7 15'"} dance floor
          </p>
        )}

        <p className="mt-8 text-center font-body text-xs text-ink-400">
          Questions about your layout? Reach out to your venue coordinator.
        </p>
      </div>
    </div>
  );
}
